/**
 * Test page for Dashboard E2E testing
 * Story 226: Projects Dashboard
 *
 * Provides mock data for testing without authentication or backend.
 */

import { useState, useCallback } from 'react';
import { ViewToggle, type ViewMode } from '../../components/dashboard/ViewToggle';
import { ProjectsGrid } from '../../components/dashboard/ProjectsGrid';
import { ProjectsList } from '../../components/dashboard/ProjectsList';
import { ProjectsEmptyState } from '../../components/dashboard/ProjectsEmptyState';
import { InaccessibleSection } from '../../components/dashboard/InaccessibleSection';
import { AddProjectModal } from '../../components/dashboard/AddProjectModal';
import type { ProjectListItem } from '../../hooks/useProjects';
import '../Dashboard.css';
import './DashboardTest.css';

// Mock data matching E2E test expectations
const MOCK_PROJECTS: ProjectListItem[] = [
  {
    id: '1',
    slug: 'myorg/frontend-app',
    name: 'myorg/frontend-app',
    github_repo_id: 12345,
    has_access: true,
    stats: {
      story_count: 42,
      in_progress_count: 7,
      done_count: 28,
      user_count: 3,
    },
    recent_users: [
      { github_username: 'johndoe' },
      { github_username: 'alice' },
      { github_username: 'bob' },
    ],
    updated_at: new Date().toISOString(),
  },
  {
    id: '2',
    slug: 'myorg/backend-api',
    name: 'myorg/backend-api',
    github_repo_id: 12346,
    has_access: true,
    stats: {
      story_count: 18,
      in_progress_count: 2,
      done_count: 10,
      user_count: 2,
    },
    recent_users: [
      { github_username: 'carol' },
      { github_username: 'johndoe' },
    ],
    updated_at: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
  },
  {
    id: '3',
    slug: 'company/shared-lib',
    name: 'company/shared-lib',
    github_repo_id: 12347,
    has_access: true,
    stats: {
      story_count: 5,
      in_progress_count: 0,
      done_count: 5,
      user_count: 1,
    },
    recent_users: [{ github_username: null }],
    updated_at: new Date(Date.now() - 86400000 * 3).toISOString(), // 3 days ago
  },
];

const MOCK_INACCESSIBLE: ProjectListItem[] = [
  {
    id: '4',
    slug: 'private-org/locked-repo',
    name: 'private-org/locked-repo',
    github_repo_id: 99999,
    has_access: false,
    stats: {
      story_count: 10,
      in_progress_count: 1,
      done_count: 5,
      user_count: 1,
    },
    recent_users: [],
    updated_at: new Date().toISOString(),
  },
];

interface TestMode {
  empty: boolean;
  showInaccessible: boolean;
}

export function DashboardTest() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [showAddProject, setShowAddProject] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [testMode, setTestMode] = useState<TestMode>({
    empty: false,
    showInaccessible: true,
  });

  const handleSelectProject = useCallback((projectSlug: string) => {
    setSelectedProject(projectSlug);
  }, []);

  const handleOpenAddProject = useCallback(() => {
    setShowAddProject(true);
  }, []);

  const handleCloseAddProject = useCallback(() => {
    setShowAddProject(false);
  }, []);

  const handleProjectAdded = useCallback(() => {
    // In test mode, just close the modal - no actual project to refresh
    setShowAddProject(false);
  }, []);

  // When empty mode is enabled, show no projects at all (accessible + inaccessible)
  const projects = testMode.empty ? [] : MOCK_PROJECTS;
  const inaccessible = testMode.empty ? [] : (testMode.showInaccessible ? MOCK_INACCESSIBLE : []);
  const isEmpty = projects.length === 0 && inaccessible.length === 0;

  return (
    <div className="dashboard-test">
      {/* Test controls */}
      <div className="dashboard-test__controls">
        <label>
          <input
            type="checkbox"
            checked={testMode.empty}
            onChange={(e) =>
              setTestMode((prev) => ({ ...prev, empty: e.target.checked }))
            }
          />
          Empty state
        </label>
        <label>
          <input
            type="checkbox"
            checked={testMode.showInaccessible}
            onChange={(e) =>
              setTestMode((prev) => ({
                ...prev,
                showInaccessible: e.target.checked,
              }))
            }
          />
          Show inaccessible
        </label>
        {selectedProject && (
          <span className="dashboard-test__selected">
            Selected: {selectedProject}
          </span>
        )}
      </div>

      {/* Dashboard content */}
      <div className="dashboard">
        <nav className="dashboard__nav">
          <div className="dashboard__nav-inner">
            <div className="dashboard__nav-brand">
              <span className="dashboard__brand-name">Ceetrix</span>
            </div>
            <div className="dashboard__nav-actions">
              <span className="dashboard__username">@testuser</span>
              <button className="dashboard__sign-out" type="button">
                Sign Out
              </button>
            </div>
          </div>
        </nav>

        <main className="dashboard__main">
          <div className="dashboard__header">
            <h2 className="dashboard__title">Projects</h2>
            <div className="dashboard__actions">
              {!isEmpty && (
                <>
                  <ViewToggle value={viewMode} onChange={setViewMode} />
                  <button
                    className="dashboard__new-project"
                    onClick={handleOpenAddProject}
                    type="button"
                  >
                    Add Project...
                  </button>
                </>
              )}
            </div>
          </div>

          {isEmpty && (
            <ProjectsEmptyState onCreateProject={handleOpenAddProject} />
          )}

          {!isEmpty && projects.length > 0 && (
            <>
              {viewMode === 'grid' ? (
                <ProjectsGrid
                  projects={projects}
                  onSelectProject={handleSelectProject}
                />
              ) : (
                <ProjectsList
                  projects={projects}
                  onSelectProject={handleSelectProject}
                />
              )}
            </>
          )}

          {inaccessible.length > 0 && (
            <InaccessibleSection projects={inaccessible} />
          )}
        </main>

        {showAddProject && (
          <AddProjectModal onClose={handleCloseAddProject} onSuccess={handleProjectAdded} />
        )}
      </div>
    </div>
  );
}
